import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { DeleteItemIcon } from '@/assets/static/icons/delete-item';
import Button from '@/components/basicComponents/button';
import { ErrorText } from '@/components/basicComponents/input';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { ThreeColumnWrapper } from '../styles';
import { IRenderTemplateRow, OpenWizardModalPageArgsType } from '../types';
import { evaluateDependencyItem, shouldRenderEmptyComponent } from '../utils';

interface BankAccountsComponentProps {
  row: IRenderTemplateRow;
  mode: 'documentationTab' | 'wizard';
  pageName?: string;
  bankAccounts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'];
  ignoreRequired: boolean;
  deleteItem: (type: 'bankAccounts' | 'contacts', id: string) => void;
  openModalPage: ({ type, row, id }: OpenWizardModalPageArgsType) => void;
}

export const maskedBankAccountFields = ['moneyTransferCode', 'beneficiaryAccountNumber', 'forFurtherCredit'];
export const bankAccountsRowNames = {
  transferType: 'Money Transfer Type',
  transferCode: 'Money Transfer Code',
  streetAddress: 'Bank Street Address'
};
export const flattenSensitiveBankAccountDataObject = (obj: { [key: string]: any }): Record<string, string> => {
  const entries = Object.entries(obj).flatMap(([key, value]) => {
    if (typeof value === 'object' && !Array.isArray(value) && value) {
      return Object.entries(value);
    } else {
      return [[key, value]];
    }
  });
  return Object.fromEntries(entries);
};

export const isBankAccountDataComplete = (
  bankAccount: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'][number]
): boolean => {
  return Object.entries(bankAccount.data).every(([key, value]) => {
    if (key === 'address') {
      return Object.entries(value).every(([key, value]) => !!value);
    }
    if (key === 'furtherCredit') return true;
    return !!value;
  });
};

export const BankAccountsComponent = ({
  row,
  mode,
  pageName = '',
  bankAccounts,
  ignoreRequired,
  deleteItem,
  openModalPage
}: BankAccountsComponentProps) => {
  const { isMobile } = useResponsive();
  const { name, dependsOn, validation, config } = row;
  const {
    formState: { errors },
    setValue,
    unregister,
    watch
  } = useFormContext();
  const bankAccountIDs = watch(`${pageName}.${name}`);
  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  const [bankAccountSelectOptions, setBankAccountSelectOptions] = useState<ISelectValue[]>([]);
  const [selectedBankAccounts, setSelectedBankAccounts] = useState<
    (Omit<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'][number], 'data'> & {
      data: Record<string, string>;
      error?: string;
    })[]
  >([]);
  const [showBankAccountsDropdown, setShowBankAccountsDropdown] = useState(false);

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      unregister(`${pageName}.${name}`);
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  useEffect(() => {
    const selectedBankAccounts = bankAccounts
      ?.filter((bankAccount) => bankAccountIDs?.includes(bankAccount.id))
      ?.map((bankAccount) => {
        // Template bank accounts (not linked to a legal entity) are never considered complete
        const isTemplateBankAccount = !bankAccount.legalEntityId;
        if (isTemplateBankAccount) {
          return {
            ...bankAccount,
            data: flattenSensitiveBankAccountDataObject(bankAccount.data),
            error: 'Template bank account selected. Please edit this bank account to proceed.'
          };
        }
        if (isBankAccountDataComplete(bankAccount)) {
          return { ...bankAccount, data: flattenSensitiveBankAccountDataObject(bankAccount.data), error: null };
        }
        return {
          ...bankAccount,
          data: flattenSensitiveBankAccountDataObject(bankAccount.data),
          error: 'Required fields missing. Please edit this bank account to proceed.'
        };
      });
    setSelectedBankAccounts(selectedBankAccounts ?? []);
  }, [bankAccountIDs, bankAccounts]);

  useEffect(() => {
    if (!bankAccounts.length) {
      setBankAccountSelectOptions([{ label: 'Select', value: '' }]);
      return;
    }

    if (config?.selectType === 'multiple') {
      setBankAccountSelectOptions(
        bankAccounts
          .filter((bankAccount) => !bankAccountIDs?.includes(bankAccount.id))
          .map((bankAccount) => ({ label: bankAccount.name, value: bankAccount.id }))
      );
      return;
    }
    setBankAccountSelectOptions(
      bankAccounts
        .filter((bankAccount) => bankAccount.id !== bankAccountIDs)
        .map((bankAccount) => ({ label: bankAccount.name, value: bankAccount.id }))
    );
  }, [bankAccounts, bankAccountIDs]);

  const handleDeleteBankAccount = (id: string) => {
    // deleteItem('bankAccounts', id);
    if (config?.selectType === 'multiple') {
      setValue(
        `${pageName}.${name}`,
        bankAccountIDs.filter((bankAccountID: string) => bankAccountID !== id)
      );
      return;
    }

    setValue(`${pageName}.${name}`, undefined);
  };

  const onChangeBankAccountSelect = (value: string) => {
    if (!value) return;

    switch (mode) {
      case 'documentationTab':
        // Template bank accounts need to be filled out immediately
        const isTemplateBankAccount = !bankAccounts.find((bankAccount) => bankAccount.id === value)?.legalEntityId;
        if (isTemplateBankAccount) {
          openModalPage({ type: 'enter-bank-account-info', row, id: value });
          break;
        }
        setShowBankAccountsDropdown(false);
        if (config?.selectType === 'multiple') {
          setValue(`${pageName}.${name}`, [...(bankAccountIDs || []), value]);
          return;
        }
        setValue(`${pageName}.${name}`, value);
        break;
      case 'wizard':
        openModalPage({ type: 'enter-bank-account-info', row, id: value });
        break;
      default:
        break;
    }
  };

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  return (
    <>
      {!selectedBankAccounts?.length && (
        <ThreeColumnWrapper isMobile={isMobile}>
          {mode === 'wizard' && (
            <Select
              label="Bank Account"
              setValue={onChangeBankAccountSelect}
              options={bankAccountSelectOptions}
              isRequiredField={!!validation?.required && !ignoreRequired}
              errorText={(errors?.[pageName] as FieldErrors)?.[name]?.message?.toString()}
              size="md"
              additionalOption={
                <AddButtonWrapper>
                  <Button size="sm" styleType="outline" type="button" onClick={() => openModalPage({ type: 'enter-bank-account-info', row })}>
                    Add New Bank Account
                  </Button>
                </AddButtonWrapper>
              }
            />
          )}
          {mode === 'documentationTab' && (
            <>
              {bankAccountSelectOptions.length ? (
                <Select
                  label="Bank Account"
                  setValue={onChangeBankAccountSelect}
                  options={bankAccountSelectOptions}
                  isRequiredField={!!validation?.required && !ignoreRequired}
                  errorText={(errors?.[pageName] as FieldErrors)?.[name]?.message?.toString()}
                  size="md"
                  additionalOption={
                    <AddButtonWrapper>
                      <Button size="sm" styleType="outline" type="button" onClick={() => openModalPage({ type: 'enter-bank-account-info', row })}>
                        Add New Bank Account
                      </Button>
                    </AddButtonWrapper>
                  }
                />
              ) : (
                <AddSectionWrapper>
                  <AddSectionText>No banks have been added yet.</AddSectionText>
                  <Button
                    onClick={() => openModalPage({ type: 'enter-bank-account-info', row })}
                    styleType="outline"
                    icon={<AddButtonIcon fill={themeVar('font.action')} />}
                    size="md"
                    width="max-content"
                  >
                    Add
                  </Button>
                </AddSectionWrapper>
              )}
            </>
          )}
        </ThreeColumnWrapper>
      )}
      <BankAccountsSectionContent>
        {selectedBankAccounts?.map((item) => (
          <div key={item.id}>
            {item?.error && <ErrorText size="sm">{item.error}</ErrorText>}
            <BankAccountsWrapper isMobile={isMobile}>
              <BankAccountsItemsWrapper>
                {item.name && (
                  <TwoColumnWrapper isMobile={isMobile}>
                    <BankAccountsItem>Beneficiary Bank Name</BankAccountsItem>
                    <BankAccountsItem isBold>{item.name}</BankAccountsItem>
                  </TwoColumnWrapper>
                )}
                {Object.entries(item.data).map(([key, value]) => (
                  <React.Fragment key={key}>
                    {!!value && key !== '__typename' && (
                      <TwoColumnWrapper isMobile={isMobile}>
                        <BankAccountsItem>{bankAccountsRowNames?.[key as keyof typeof bankAccountsRowNames] ?? _.startCase(key)}</BankAccountsItem>
                        <BankAccountsItem isBold>
                          {maskedBankAccountFields.includes(key) ? (value as string).replace(/./g, '*') : (value as string)}
                        </BankAccountsItem>
                      </TwoColumnWrapper>
                    )}
                  </React.Fragment>
                ))}
              </BankAccountsItemsWrapper>
              <ButtonsWrapper id="editButton" isMobile={isMobile}>
                <DeleteIconWrapper isMobile={isMobile} onClick={() => handleDeleteBankAccount(item.id)}>
                  <DeleteItemIcon width={23} height={23} fill={themeVar('context.error')} />
                </DeleteIconWrapper>
                <EditIconWrapper
                  isMobile={isMobile}
                  id="editButton"
                  onClick={() => openModalPage({ type: 'enter-bank-account-info', row, id: item.id })}
                >
                  <EditTodoIcon />
                </EditIconWrapper>
              </ButtonsWrapper>
            </BankAccountsWrapper>
            {row?.config?.selectType === 'multiple' && <Divider />}
          </div>
        ))}
        {!!(selectedBankAccounts?.length && row?.config?.selectType === 'multiple') && (
          <Button
            onClick={() => openModalPage({ type: 'enter-bank-account-info', row })}
            styleType="outline"
            icon={<AddButtonIcon fill={themeVar('font.action')} />}
            size="md"
            width="max-content"
          >
            Add
          </Button>
        )}
      </BankAccountsSectionContent>
    </>
  );
};

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const BankAccountsSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 20px 0;
  background-color: ${themeVar('border.base')};
`;

const BankAccountsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  padding: ${({ isMobile }) => (isMobile ? '0' : '20px')};
  transition: 0.1s ease-in-out;

  @media (hover: hover) {
    &:hover #editButton {
      opacity: 1;
    }

    &:hover {
      background-color: ${themeVar('layer.12')};
    }
  }
`;

const BankAccountsItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
`;

const BankAccountsItem = styled.div<{ isBold?: boolean }>`
  font-family: Blinker;
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 4px;
`;

const ButtonsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  gap: 10px;
  margin-left: ${({ isMobile }) => (isMobile ? '0' : 'auto')};
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
  transition: 0.1s ease-in-out;
`;

const EditIconWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  padding: 12px;
  border-radius: 4px;
  background-color: ${themeVar('action.default')};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  cursor: pointer;
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
`;

const DeleteIconWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  height: 40px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid ${themeVar('context.error')};
`;

const AddSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 10px;
`;

const AddSectionText = styled.p`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;
